import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AxiosError } from 'axios';

/**
 * Hook to check if the current user has Operator access (allowlisted operator).
 * Returns true only if the user is both a platform admin AND in the server's operator allowlist.
 * This is used for non-discoverability: don't show operator console UI unless user is allowlisted.
 */
export function useSystemAdminAccess() {
  const { data, isLoading, error } = useQuery<{ allowed: boolean }, AxiosError>({
    queryKey: ['admin', 'access-check'],
    queryFn: async () => {
      const response = await api.get('/admin/access-check');
      return response.data;
    },
    retry: false, // Don't retry - 404 means not allowed
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  // If we get a 404, user is not allowed (non-discoverability)
  const isNotAllowed = error?.response?.status === 404;
  const isAllowed = data?.allowed === true && !isNotAllowed;

  return {
    isSystemAdmin: isAllowed,
    isLoading,
    // Don't expose the actual error - just that access is denied
    isAccessDenied: isNotAllowed || (!isLoading && !isAllowed),
  };
}
